import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
// Importamos la configuración de toast para mantener los estilos
import { customToastConfig } from '../utils/toastConfig'; 
import toast from 'react-hot-toast'; 

// --- CONSTANTE DE TIEMPO (10 segundos para DEBUGGING) ---
// Para volver a 2 minutos, usa: 120 * 1000 = 120000;
// Para volver a 15 minutos, usa: 900 * 1000 = 900000;
const INACTIVITY_LIMIT_MS = 900000; 
const TOAST_VISIBLE_DURATION_MS = 5000; // 5 segundos para el pop-up
const LOGOUT_DELAY_MS = 5500; // 5.5 segundos antes de redirigir

export const useInactivityTimeout = () => {
    const { isAuthenticated, logout } = useAuth();
    const timeoutRef = useRef<number | null>(null);
    const limitSeconds = INACTIVITY_LIMIT_MS / 1000;

    // Función que limpia el temporizador y lo reinicia (cuando hay actividad)
    const resetTimer = useCallback((event: Event | null = null) => {
        if (!isAuthenticated) return; // Si no está autenticado, no hacer nada

        if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
            // Log para ver qué evento reinició el timer
            if (event) {
                console.log(`[TIMEOUT - RESET] Timer CLARO. Reiniciado por: ${event.type}. Nuevo timeout en ${limitSeconds}s.`);
            } else {
                console.log(`[TIMEOUT - RESET] Timer CLARO. Inicializado. Nuevo timeout en ${limitSeconds}s.`);
            }
        } else {
            console.log(`[TIMEOUT - INICIO] Primer inicio de temporizador. Timeout en ${limitSeconds}s.`);
        }

        // Configurar el nuevo temporizador (el temporizador de inactividad)
        timeoutRef.current = window.setTimeout(() => {
            console.log(`[TIMEOUT - EXPIRADO] ¡TEMPORIZADOR EXPIRADO! Ejecutando lógica de cierre...`);
            
            // 1. Mostrar el pop-up con la duración deseada (5 segundos)
            const toastId = toast.error(
                "Sesión cerrada por inactividad. Serás redirigido al inicio de sesión.", 
                {
                    ...customToastConfig.error, // Usar estilos base
                    duration: TOAST_VISIBLE_DURATION_MS, // Sobrescribir duración
                    id: 'inactivity-logout-toast' // ID para posible limpieza
                }
            );

            // 2. Esperar un momento (5.5 segundos) antes de ejecutar el logout y la navegación
            window.setTimeout(() => {
                console.log(`[TIMEOUT - REDIRECCIÓN] Ejecutando logout y navegación...`);
                toast.dismiss(toastId); // Limpiar el toast antes de la navegación
                logout();
            }, LOGOUT_DELAY_MS); 

        }, INACTIVITY_LIMIT_MS);
    }, [isAuthenticated, logout, limitSeconds]);

    useEffect(() => {
        // Log al montar/cambiar el estado de autenticación
        console.log(`[TIMEOUT - ESTADO] Auth status: isAuthenticated = ${isAuthenticated}.`);
        
        // Iniciar el temporizador al cargar si estamos autenticados
        if (isAuthenticated) {
            resetTimer(null);
        }

        // Eventos de actividad que reiniciarán el temporizador
        const events = ['mousemove', 'keydown', 'touchstart', 'scroll'];

        if (isAuthenticated) {
            // Adjuntar los listeners
            events.forEach(event => {
                window.addEventListener(event, resetTimer);
            });
            console.log(`[TIMEOUT - LISTENERS] Listeners de actividad adjuntos.`);
        }

        // Limpieza: remover listeners y el temporizador al desmontar
        return () => {
            console.log("[TIMEOUT - LIMPIEZA] Limpiando listeners y temporizador.");
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
            }
            events.forEach(event => {
                window.removeEventListener(event, resetTimer);
            });
            timeoutRef.current = null; 
        };
    }, [resetTimer, isAuthenticated]); 
    
    return null;
};